import { CreateCourseRequirement, DeleteCourseRequirement, UpdateCourseRequirement } from '@/connection/courses';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { ICourse, ICourseRequirement, ICreateCourseRequirement, type CourseRequirementType } from '@/types/courses';
import { BrandliveEvent } from '@/types/projects.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { useCallback, useState } from 'react';
import { getRequirementMapBySession } from '../utils/get-requirement-map-by-session';
import { DEFAULT_NEW_REQUIREMENT, defaultSettings } from '../utils/course-requirement-defaults';
import { selectableRequirementTypes } from '../utils/requirement-labels';
import { EToastTypes } from '@ui/toast/toast.types';

type UseCourseRequirementProps = {
  course: ICourse | null;
  setCourse: (course: ICourse) => void;
};

const useCourseRequirement = ({ course, setCourse }: UseCourseRequirementProps) => {
  const user = useAppSelector(getUser);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentRequirement, setCurrentRequirement] = useState<ICreateCourseRequirement>(DEFAULT_NEW_REQUIREMENT);
  const [requirementWorking, setRequirementWorking] = useState(false);
  const [requirementToUpdate, setRequirementToUpdate] = useState<string | null>(null);
  const [requirementToDelete, setRequirementToDelete] = useState<string | null>(null);

  const canSaveNewRequirement = (requirement: ICreateCourseRequirement) =>
    Boolean(requirement.session_id && requirement.required_amount);

  const openRequirementModal = useCallback((requirement?: ICreateCourseRequirement) => {
    setCurrentRequirement(requirement || DEFAULT_NEW_REQUIREMENT);
    setModalOpen(true);
  }, []);

  const closeRequirementModal = useCallback(() => {
    setModalOpen(false);
    setCurrentRequirement(DEFAULT_NEW_REQUIREMENT);
    setRequirementToUpdate(null);
  }, []);

  const handleCreateRequirement = useCallback(async () => {
    if (!user?.active_channel || !course || !currentRequirement) return;

    setRequirementWorking(true);

    try {
      const createdRequirement = await CreateCourseRequirement(user.active_channel, course.uuid, currentRequirement);
      setCourse({
        ...course,
        requirements: [...(course.requirements || []), createdRequirement],
      });
      closeRequirementModal();
    } catch {
      toastTrigger('Error adding course requirement', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }

    setRequirementWorking(false);
  }, [user?.active_channel, course, currentRequirement, setCourse, closeRequirementModal]);

  const handleUpdateRequirement = useCallback(async () => {
    if (!user?.active_channel || !course || !currentRequirement || !requirementToUpdate) return;

    setRequirementWorking(true);

    try {
      const updatedRequirement = await UpdateCourseRequirement(
        user.active_channel,
        course.uuid,
        requirementToUpdate,
        currentRequirement
      );
      setCourse({
        ...course,
        requirements: course.requirements?.map((req) => (req.uuid === requirementToUpdate ? updatedRequirement : req)),
      });
      closeRequirementModal();
    } catch {
      toastTrigger('Error updating course requirement', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }

    setRequirementWorking(false);
  }, [user?.active_channel, course, currentRequirement, requirementToUpdate, setCourse, closeRequirementModal]);

  const handleDeleteRequirement = useCallback(async () => {
    if (!user?.active_channel || !course || !requirementToDelete) return;

    const requirement = course.requirements?.find((req) => req.uuid === requirementToDelete);
    if (!requirement) return;

    try {
      setRequirementWorking(true);
      await DeleteCourseRequirement(user.active_channel, course.uuid, requirementToDelete);
      setCourse({
        ...course,
        requirements: course.requirements?.filter((req) => req.uuid !== requirementToDelete),
      });
      setRequirementToDelete(null);
    } catch {
      toastTrigger('Error deleting course requirement', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    }
    setRequirementWorking(false);
  }, [user?.active_channel, course, requirementToDelete, setCourse]);

  const handleEditRequirement = useCallback((requirement: ICourseRequirement) => {
    setRequirementToUpdate(requirement.uuid);
    setCurrentRequirement(requirement);
    setModalOpen(true);
  }, []);

  const canAddRequirement = (
    requirements: ICreateCourseRequirement[] | undefined,
    workingEvent: BrandliveEvent | undefined | null
  ) => {
    if (!workingEvent) {
      return false;
    }

    if (!requirements) {
      return true;
    }

    // create map to help prevent nested loops
    const requirementsMap = getRequirementMapBySession(requirements);

    // a session can carry at most one requirement of each selectable type
    for (const session of workingEvent.sessions) {
      const sessionReqs = requirementsMap[session.session];
      const addedReqs = new Set<CourseRequirementType>(sessionReqs);

      // you can add more if any session does not have every single type of requirement added
      if (addedReqs.size < selectableRequirementTypes.length) {
        return true;
      }
    }

    return false;
  };

  return {
    modalOpen,
    currentRequirement,
    requirementWorking,
    requirementToUpdate,
    requirementToDelete,
    setCurrentRequirement,
    setRequirementToDelete,
    openRequirementModal,
    closeRequirementModal,
    handleCreateRequirement,
    handleUpdateRequirement,
    handleDeleteRequirement,
    handleEditRequirement,
    canAddRequirement,
    canSaveNewRequirement,
    defaultSettings,
    defaultNewRequirement: DEFAULT_NEW_REQUIREMENT,
  };
};

export default useCourseRequirement;
